import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  House, Users, Cube, FileText, CheckCircle, SignOut,
  UserCircleGear, AddressBook, ClockCounterClockwise, Bell, GearSix,
  CaretDoubleLeft, CaretDoubleRight, CaretDown, CaretRight, Target, Envelope, ClipboardText, UsersThree, UsersFour, Binoculars, Handshake, MapPin,
  Shield, ChartPie, ChartLineUp, Buildings, Wallet, Broom, Briefcase, Sparkle, Archive, Kanban, Coins
} from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import { useTenantPath } from '../lib/tenantPath';
import ImpersonationBanner from './ImpersonationBanner';
import ReviewReminderBanner from './ReviewReminderBanner';
import TenderDocsBanner from './TenderDocsBanner';
import CopilotWidget from './copilot/CopilotWidget';
import IdleTimeoutManager from './IdleTimeoutManager';
import NotificationBell from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user, logout } = useAuthStore();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved !== null) return saved === '1';
    // Default: auto-collapse on screens smaller than lg breakpoint (1024px)
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  });

  // Auto-collapse/expand on viewport resize while the user hasn't manually overridden
  useEffect(() => {
    const handler = () => {
      const userPref = localStorage.getItem('sidebar_collapsed_manual');
      if (userPref === '1') return; // user has taken manual control, don't auto-toggle
      setCollapsed(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    // Show Vooxy branding ONLY on the platform console route (`/super-admin`).
    // On tenant routes (`/t/<slug>/*`) — including when a super-admin browses
    // into one — always fetch the tenant's company_settings so their logo and
    // name appear correctly.
    const onConsole = location.pathname.startsWith('/super-admin');
    if (user?.is_super_admin && onConsole) {
      setCompanyLogo(null);
      setCompanyName('Vooxy');
      document.title = 'Vooxy Console';
      document.querySelectorAll("link[rel*='icon']").forEach(l => l.parentNode.removeChild(l));
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = '/favicon.svg?v=vooxy-cat-3';
      document.head.appendChild(link);
      const appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      appleLink.href = '/favicon.svg?v=vooxy-cat-3';
      document.head.appendChild(appleLink);
      return;
    }
    axios.get(`${API}/company-settings`).then(resp => {
      if (resp.data?.logo_url) setCompanyLogo(resp.data.logo_url);
      if (resp.data?.company_name) setCompanyName(resp.data.company_name);

      // Dynamically rebrand the browser tab — title + favicon
      const name = resp.data?.company_name?.trim();
      if (name) {
        document.title = `${name} — CRM`;
      }
      const logo = resp.data?.logo_url;
      const fullLogoUrl = logo
        ? (logo.startsWith('http') ? logo : `${BACKEND_URL}${logo}`)
        : '/favicon.svg?v=vooxy-cat-3'; // ALWAYS enforce a favicon — never leave the browser's cached one
      // Remove existing favicon links to force browser refresh (Chrome caches aggressively)
      document.querySelectorAll("link[rel*='icon']").forEach(l => l.parentNode.removeChild(l));
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = fullLogoUrl;
      document.head.appendChild(link);
      const appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      appleLink.href = fullLogoUrl;
      document.head.appendChild(appleLink);
    }).catch(() => {});
  }, [user?.is_super_admin, location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Poll unread notification count every 30s
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let active = true;
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/notifications/mine`, { params: { unread_only: true } });
        if (active) setUnreadCount(r.data.unread_count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [location.pathname]);

  const navItems = [];
  // Vooxy platform chrome (dark sidebar, "Tenant Admin" nav only) applies ONLY when the
  // super-admin is on the platform console itself (`/super-admin`). If the same super-admin
  // navigates into a tenant path (`/t/<slug>/*`), they get the tenant's regular sidebar so
  // the tenant's feature set stays accessible.
  const onPlatformConsole = location.pathname.startsWith('/super-admin');
  const isSuperConsole = !!user?.is_super_admin && onPlatformConsole;

  if (isSuperConsole) {
    navItems.push({ path: '/super-admin', icon: Buildings, label: 'Tenant Admin' });
  } else {
    navItems.push({ path: '/dashboard', icon: House, label: 'Dashboard', group: 'analytics' });
    navItems.push({ path: '/my-notifications', icon: Bell, label: 'Notifications', badge: unreadCount });
    navItems.push({ path: '/customers', icon: AddressBook, label: 'Customers' });
    navItems.push({ path: '/leads', icon: Users, label: 'Lead & Quotation' });
    // iter89 — Tenders module for TE + Managers + VP+. SEs get access too because
    // per user directive they can see tenders in their region.
    if (['Tender Executive', 'Sales Executive', 'Regional Sales Manager',
         'National Sales Manager', 'Vice President', 'President', 'Sales Coordinator'].includes(user?.role)) {
      navItems.push({ path: '/tenders', icon: Briefcase, label: 'All Tenders', group: 'tenders' });
      // iter128 — moved from visually-nested items to a proper collapsible
      // group so the sidebar behaves consistently with Analytics/Team/Admin.
      navItems.push({ path: '/tenders/feasibility-profile', icon: ClipboardText, label: 'Feasibility Profile', group: 'tenders' });
      // Performance Archive stays TE+manager only (SE excluded per iter108).
      // Feb 2026 — SC gets view-only access as part of their NSM-level read scope.
      if (['Tender Executive', 'Regional Sales Manager', 'National Sales Manager',
           'Vice President', 'President', 'Sales Coordinator'].includes(user?.role)) {
        navItems.push({ path: '/tenders/performance-archive', icon: Archive, label: 'Performance Archive', group: 'tenders' });
      }
    }
    navItems.push({ path: '/reviews', icon: Target, label: 'Team Reviews', group: 'team' });
    navItems.push({ path: '/products', icon: Cube, label: 'Products' });
    navItems.push({ path: '/approvals', icon: CheckCircle, label: 'My Approvals' });
    // iter88 — Proforma Invoices & GST Exemptions merged into "My Approvals"
    // as tabs. Sidebar entries removed to declutter; direct routes still work.
    navItems.push({ path: '/goals', icon: Target, label: 'Goals', group: 'team' });
    // iter119 — Daily Activity nav entry. Visible to mandate-eligible roles
    // (SE/TE/RSM/NSM) so they log; also visible to VP+ so they open the
    // team matrix and toggle mandates. Anyone else is a no-op — the page
    // itself renders an empty-state for out-of-scope roles.
    if (['Sales Executive', 'Tender Executive', 'Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President', 'Sales Coordinator'].includes(user?.role)) {
      navItems.push({ path: '/team/daily-activity', icon: MapPin, label: 'Daily Activity', group: 'team' });
    }
    // iter92 — Tender Executive is a tender-focused role; hide the general
    // sales-motion tabs (Interactions, Competition Mapping) from their sidebar
    // to keep the workspace focused. They still see Leads & Quotation and
    // Quotations tabs read-only (create/edit is gated on those pages).
    if (user?.role !== 'Tender Executive') {
      navItems.push({ path: '/competition', icon: Binoculars, label: 'Competition Mapping' });
      navItems.push({ path: '/interactions', icon: Handshake, label: 'Interactions' });
    }
    navItems.push({ path: '/collections', icon: Wallet, label: 'Collections' });
    navItems.push({ path: '/memos', icon: ClipboardText, label: 'Memos', group: 'team' });
  }

  // Manager-only entries (RSM and above see the My Team tab) — never on the platform console.
  // Feb 2026 — Sales Coordinator is view-only but inherits the full tenant read scope,
  // so they also see My Team (writes are blocked server-side on anything they don't own).
  if (!isSuperConsole && ['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President', 'Sales Coordinator'].includes(user?.role)) {
    navItems.push({ path: '/team', icon: UsersThree, label: 'My Team', group: 'team' });
  }

  // Analytics tab — VP+ by default. Feb 2026 plan: Sales Coordinator will get
  // view-only access to this group in Phase 2 (with GM number masking + the
  // President-togglable permission flags). Deliberately NOT added to the SC
  // sidebar yet so we don't leak raw COGS / email logs before the masking
  // layer ships.
  if (!isSuperConsole && ['Vice President', 'President'].includes(user?.role)) {
    navItems.push({ path: '/gross-margin', icon: ChartPie, label: 'Gross Margin', group: 'analytics' });
    navItems.push({ path: '/ai-feedback', icon: Sparkle, label: 'AI Feedback', group: 'analytics' });
    navItems.push({ path: '/tender-workload', icon: Kanban, label: 'Tender Workload', group: 'analytics' });
    navItems.push({ path: '/notifications', icon: Envelope, label: 'Email Log', group: 'admin' });
    navItems.push({ path: '/import-history', icon: ClockCounterClockwise, label: 'Import History', group: 'admin' });
    navItems.push({ path: '/profitability', icon: Coins, label: 'Profitability', group: 'admin' });
  }

  if (!isSuperConsole && user?.role === 'President') {
    navItems.push({ path: '/data-quality', icon: Broom, label: 'Data Quality', group: 'analytics' });
    navItems.push({ path: '/company-settings', icon: GearSix, label: 'Company Settings', group: 'admin' });
    navItems.push({ path: '/users', icon: UserCircleGear, label: 'User Management', group: 'admin' });
    navItems.push({ path: '/audit-logs', icon: Shield, label: 'Audit Logs', group: 'admin' });
  }

  // iter117/118 — Sidebar group config. Items with a matching `group: <key>`
  // tag get collected into a single collapsible parent. `min` guards against
  // pointlessly wrapping a single item (e.g., a Sales Exec has only Dashboard
  // in the analytics group — we render it as a flat entry, no collapse).
  const NAV_GROUPS = [
    {
      key: 'analytics',
      label: 'Analytics',
      icon: ChartLineUp,
      order: ['Dashboard', 'Gross Margin', 'AI Feedback', 'Tender Workload', 'Data Quality'],
      min: 2,
    },
    {
      key: 'team',
      label: 'Team',
      icon: UsersFour,
      order: ['My Team', 'Daily Activity', 'Goals', 'Team Reviews', 'Memos'],
      min: 2,
    },
    {
      // iter128 — Tenders parent + its two child screens (Feasibility Profile,
      // Performance Archive). Keeps sibling nav flat and matches how
      // Analytics/Team/Admin behave.
      key: 'tenders',
      label: 'Tenders',
      icon: Briefcase,
      order: ['All Tenders', 'Feasibility Profile', 'Performance Archive'],
      min: 2,
    },
    {
      // iter126 — bundles Email Log, Import History, Company Settings,
      // User Management and Audit Logs under a single collapsible parent so
      // the sidebar tail stops feeling like a landfill of admin-only items.
      key: 'admin',
      label: 'Admin',
      icon: GearSix,
      order: ['Profitability', 'Email Log', 'Import History', 'Company Settings', 'User Management', 'Audit Logs'],
      min: 2,
    },
  ];

  // Build the rendered nav list: the first tagged item's position becomes
  // the group's slot; the other tagged items are dropped from the flat list
  // (they render as children under the group block). When the sidebar is
  // collapsed we skip the grouping and render every entry as a plain icon —
  // grouping only helps when labels are visible.
  const groupsMeta = NAV_GROUPS.map((G) => {
    const children = G.order
      .map((label) => navItems.find((x) => x.label === label))
      .filter(Boolean);
    return { ...G, children, enabled: children.length >= G.min };
  });
  const groupByKey = Object.fromEntries(groupsMeta.map((g) => [g.key, g]));

  const renderedItems = [];
  const groupInserted = {};
  for (const item of navItems) {
    const g = item.group ? groupByKey[item.group] : null;
    // Groups only apply in expanded mode + when the group actually has
    // enough children to be worth wrapping.
    if (!collapsed && g && g.enabled) {
      if (!groupInserted[g.key]) {
        renderedItems.push({ __group: g });
        groupInserted[g.key] = true;
      }
      continue; // skip — rendered inside the group block
    }
    renderedItems.push(item);
  }

  // Persisted open/closed state per group.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_open_groups') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem('sidebar_open_groups', JSON.stringify(openGroups));
  }, [openGroups]);
  // Auto-open a group whenever one of its children is the active route,
  // BUT respect an explicit user toggle. If the user has clicked the parent
  // to close it, keep it closed even while on a child page — they made a
  // deliberate choice. `undefined` in state means "no explicit choice yet"
  // so we fall back to the auto-open default.
  const isGroupActive = (g) =>
    g.children.some((c) => location.pathname === tp(c.path) || location.pathname.endsWith(c.path));
  const isGroupOpen = (g) => {
    const explicit = openGroups[g.key];
    if (explicit === true) return true;
    if (explicit === false) return false;
    return isGroupActive(g);
  };
  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !isGroupOpen(groupByKey[key]) }));

  const handleLogout = () => {
    logout();
    // Marketing home; the subdomain modal there lets the user log back in.
    navigate('/');
  };


  return (
    <div className="flex flex-col h-screen bg-[#F4F9F7]" data-testid="layout">
      {/* Impersonation banner — self-hides unless the current session is a
          super-admin acting as a tenant user. Pinned above sidebar+main so it
          spans the FULL viewport width. */}
      <ImpersonationBanner />
      <ReviewReminderBanner />
      <TenderDocsBanner />
      <IdleTimeoutManager />

      <div className="flex flex-1 min-h-0">
        <aside
          className={`relative ${collapsed ? 'w-16' : 'w-64'} ${isSuperConsole ? 'bg-[#050B18] border-r border-white/10' : 'bg-white border-r border-[#D5E8E4]'} flex flex-col h-full transition-[width] duration-300 ease-in-out`}
          data-testid="sidebar"
          data-collapsed={collapsed ? 'true' : 'false'}
        >
        {/* Collapse/Expand toggle */}
        <button
          onClick={() => {
            localStorage.setItem('sidebar_collapsed_manual', '1');
            setCollapsed(c => !c);
          }}
          className={`absolute -right-3 top-6 z-20 w-6 h-6 rounded-full ${isSuperConsole ? 'bg-[#0A1428] border border-white/20 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-400' : 'bg-white border border-[#D5E8E4] shadow-sm text-[#00a69c] hover:bg-[#E2F3F0] hover:border-[#00a69c]'} flex items-center justify-center transition`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-toggle-btn"
        >
          {collapsed ? <CaretDoubleRight size={12} weight="bold" /> : <CaretDoubleLeft size={12} weight="bold" />}
        </button>

        <div className={`shrink-0 ${collapsed ? 'p-3' : 'p-5'}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`} data-testid="app-title">
            {isSuperConsole ? (
              // Super-admin platform console: Vooxy brand, never a tenant's logo.
              <img src="/vooxy-logo.svg" alt="Vooxy" className="w-10 h-10 shrink-0" data-testid="sidebar-vooxy-logo" />
            ) : companyLogo ? (
              <img src={companyLogo.startsWith('http') ? companyLogo : `${BACKEND_URL}${companyLogo}`} alt={companyName} className="w-10 h-10 object-contain rounded shrink-0" data-testid="sidebar-company-logo" />
            ) : (
              // Fallback — a clean single-letter mark on the tenant's name. NO
              // hardcoded "VOPA" logo; that was leftover branding from before
              // Vooxy was the platform brand.
              <div className="w-10 h-10 shrink-0 rounded-lg bg-gradient-to-br from-[#34D399] to-[#00a69c] grid place-items-center text-white font-black" style={{ fontFamily: 'Outfit' }} data-testid="sidebar-company-logo-fallback">
                {(companyName || 'V').charAt(0).toUpperCase()}
              </div>
            )}
            {!collapsed && (
              <div className="overflow-hidden">
                <h1 className={`text-xl font-bold leading-tight truncate ${isSuperConsole ? 'text-white' : 'text-[#00a69c]'}`} style={{ fontFamily: 'Outfit' }}>
                  {isSuperConsole ? 'Vooxy' : companyName}
                </h1>
                <p className={`text-[10px] leading-tight ${isSuperConsole ? 'text-slate-500 uppercase tracking-widest' : 'text-[#6D8E87]'}`}>
                  {isSuperConsole ? 'Platform Console' : 'powered by Vooxy'}
                </p>
              </div>
            )}
          </div>
        </div>

        <nav className={`${collapsed ? 'px-2' : 'px-3'} flex-1 overflow-y-auto overflow-x-hidden`} data-testid="nav-menu">
          {renderedItems.map((item) => {
            // iter117 — collapsible group parent (rendered ONLY when sidebar
            // is expanded; collapsed sidebar shows children as flat icons).
            if (item.__group) {
              const G = item.__group;
              const GIcon = G.icon;
              const groupActive = isGroupActive(G);
              const open = isGroupOpen(G);
              const groupHasBadge = G.children.some((c) => c.badge > 0);
              return (
                <div key={`group-${G.key}`} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => toggleGroup(G.key)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm ${
                      groupActive
                        ? (isSuperConsole ? 'text-emerald-400 font-medium' : 'text-[#00a69c] font-medium')
                        : (isSuperConsole ? 'text-slate-300 hover:bg-white/5' : 'text-[#3B5A54] hover:bg-[#F4F9F7]')
                    }`}
                    aria-expanded={open}
                    data-testid={`nav-group-${G.key}`}
                  >
                    <span className="relative inline-flex items-center justify-center">
                      <GIcon size={18} weight={groupActive ? 'fill' : 'regular'} />
                      {groupHasBadge && !open && (
                        <span className="absolute -top-1.5 -right-2 min-w-[8px] h-2 bg-red-500 rounded-full" />
                      )}
                    </span>
                    <span className="truncate flex-1 text-left">{G.label}</span>
                    {open
                      ? <CaretDown size={14} weight="bold" className="opacity-60" />
                      : <CaretRight size={14} weight="bold" className="opacity-60" />}
                  </button>
                  {open && (
                    <div className="mt-0.5">
                      {G.children.map((child) => {
                        const CIcon = child.icon;
                        const cActive = location.pathname === tp(child.path) || location.pathname.endsWith(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={tp(child.path)}
                            className={`flex items-center gap-3 pl-8 pr-4 py-1.5 ml-4 border-l border-[#D5E8E4] rounded-r-lg mb-0.5 transition-all duration-200 text-[13px] ${
                              cActive
                                ? (isSuperConsole ? 'bg-emerald-500/15 text-emerald-400 font-medium' : 'bg-[#E2F3F0] text-[#00a69c] font-medium')
                                : (isSuperConsole ? 'text-slate-300 hover:bg-white/5' : 'text-[#3B5A54] hover:bg-[#F4F9F7]')
                            }`}
                            data-testid={`nav-${child.label.toLowerCase().replace(/ /g, '-')}`}
                          >
                            <CIcon size={15} weight={cActive ? 'fill' : 'regular'} />
                            <span className="truncate">{child.label}</span>
                            {child.badge > 0 && (
                              <span className="ml-auto min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                                {child.badge > 99 ? '99+' : child.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const Icon = item.icon;
            // Active state matches by suffix so it works under /t/<slug>/ prefix.
            const isActive = location.pathname === tp(item.path) || location.pathname.endsWith(item.path);
            // iter116 — visual nesting for sub-items (Feasibility / Performance
            // under Tenders). Only indent when the sidebar is expanded — when
            // collapsed we only show icons and don't want an obvious step-in.
            const nestedPad = item.nested && !collapsed ? 'ml-4 pl-4 border-l border-[#D5E8E4]' : '';
            const nestedSize = item.nested ? 15 : 18;
            return (
              <Link
                key={item.path}
                to={tp(item.path)}
                title={collapsed ? item.label : ''}
                className={`flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-4'} ${item.nested && !collapsed ? 'py-1.5 text-[13px]' : 'py-2.5 text-sm'} rounded-lg mb-0.5 transition-all duration-200 ${nestedPad} ${
                  isActive
                    ? (isSuperConsole ? 'bg-emerald-500/15 text-emerald-400 font-medium' : 'bg-[#E2F3F0] text-[#00a69c] font-medium')
                    : (isSuperConsole ? 'text-slate-300 hover:bg-white/5' : 'text-[#3B5A54] hover:bg-[#F4F9F7]')
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <span className="relative inline-flex items-center justify-center">
                  <Icon size={nestedSize} weight={isActive ? 'fill' : 'regular'} />
                  {item.badge > 0 && (
                    <span className={`absolute ${collapsed ? '-top-1 -right-1' : '-top-1.5 -right-2'} min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none`}
                      data-testid={`nav-badge-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className={`shrink-0 ${isSuperConsole ? 'border-t border-white/10 bg-[#050B18]' : 'border-t border-[#D5E8E4] bg-white'} ${collapsed ? 'p-2' : 'p-4'}`}>
          {user && !collapsed && (
            <Link
              to={tp('/profile')}
              className={`flex items-center gap-3 mb-3 px-2 py-1.5 -mx-2 rounded-lg transition group ${isSuperConsole ? 'hover:bg-white/5' : 'hover:bg-[#F4F9F7]'}`}
              data-testid="user-profile"
              title="Edit my profile"
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 transition ${isSuperConsole ? 'bg-emerald-500/20 text-emerald-400 group-hover:ring-2 group-hover:ring-emerald-400/30' : 'bg-[#E2F3F0] text-[#00a69c] group-hover:ring-2 group-hover:ring-[#00a69c]/30'}`}>
                {user.name?.charAt(0)}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className={`text-sm font-medium truncate transition ${isSuperConsole ? 'text-white group-hover:text-emerald-400' : 'text-[#052A23] group-hover:text-[#00a69c]'}`} data-testid="user-name">{user.name}</p>
                <p className={`text-xs truncate ${isSuperConsole ? 'text-slate-500' : 'text-[#6D8E87]'}`} data-testid="user-role">{user.is_super_admin ? 'Platform Admin' : user.role}</p>
              </div>
            </Link>
          )}
          {user && collapsed && (
            <Link
              to={tp('/profile')}
              className="flex justify-center mb-2"
              data-testid="user-profile"
              title={`${user.name} (${user.role}) — Edit profile`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition ${isSuperConsole ? 'bg-emerald-500/20 text-emerald-400 hover:ring-2 hover:ring-emerald-400/30' : 'bg-[#E2F3F0] text-[#00a69c] hover:ring-2 hover:ring-[#00a69c]/30'}`}>
                {user.name?.charAt(0)}
              </div>
            </Link>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? 'Logout' : ''}
            className={`flex items-center ${collapsed ? 'justify-center px-0' : 'gap-2 px-4'} w-full py-2 rounded-lg transition text-sm ${isSuperConsole ? 'text-red-400 hover:bg-red-500/10' : 'text-[#EF4444] hover:bg-red-50'}`}
            data-testid="logout-button"
          >
            <SignOut size={18} />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto" data-testid="main-content">
        <NotificationBell />
        {children}
      </main>
      </div>
      {/* Vooxy Copilot — floating assistant (add-on #10) */}
      <CopilotWidget />
    </div>
  );
};

export default Layout;
